import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { SaveOutlined } from '@ant-design/icons';
import {
	Row,
	Col,
	Space,
	notification,
	Spin,
	Divider,
	AutoComplete,
} from 'antd';
import {
	getGlobalLLMConfigurationApi,
	getGlobalLLMConfigurationReset,
} from '../../actions/getGlobalLLMConfigurationAction';
import {
	updateGlobalLLMConfigurationApi,
	updateGlobalLLMConfigurationReset,
} from '../../actions/updateGlobalLLMConfigurationAction';
import {
	getLLMProvidersApi,
	getLLMProvidersReset,
} from '../../actions/getLLMProvidersAction';
import {
	XThemeForm,
	XThemeSelect,
} from '../../styles/GlobalThemeStyle';
import { XTheme2Button } from '../../styles/GlobalThemeStyleCQ';
import { XTheme2InnerCard } from '../Dashboard/DashboardStyleCQ';

// Configuration items with their labels and field names
const LLM_CONFIG_ITEMS = [
	{
		label: 'Denial Letter',
		providerField: 'denial_letter_provider',
		modelField: 'denial_letter_model',
	},
	{
		label: 'Document Summarizer',
		providerField: 'doc_summarizer_provider',
		modelField: 'doc_summarizer_model',
	},
	{
		label: 'Clinical Info Extractor',
		providerField: 'clinical_info_extractor_provider',
		modelField: 'clinical_info_extractor_model',
	},
	{
		label: 'Clinical Info Extraction Evaluator',
		providerField: 'clinical_info_extractor_eval_provider',
		modelField: 'clinical_info_extractor_eval_model',
	},
	{
		label: 'Coding Evaluator',
		providerField: 'coding_eval_provider',
		modelField: 'coding_eval_model',
	},
	{
		label: 'Coverage Analyser',
		providerField: 'coverage_analyser_provider',
		modelField: 'coverage_analyser_model',
	},
	{
		label: 'State Law Checker',
		providerField: 'state_law_checker_provider',
		modelField: 'state_law_checker_model',
	},
	{
		label: 'Appeal Letter',
		providerField: 'appeal_letter_provider',
		modelField: 'appeal_letter_model',
	},
	{
		label: 'Get Intent',
		providerField: 'get_intent_provider',
		modelField: 'get_intent_model',
	},
];

const GlobalLLMConfigurationManagement = (props) => {
	const {
		getGlobalLLMConfiguration,
		getGlobalLLMConfigurationReset,
		getGlobalLLMConfigurationState,
		updateGlobalLLMConfiguration,
		updateGlobalLLMConfigurationReset,
		updateGlobalLLMConfigurationState,
		getLLMProviders,
		getLLMProvidersState,
	} = props;

	const [form] = XThemeForm.useForm();
	const [formData, setFormData] = useState({});
	const [availableModels, setAvailableModels] = useState({});
	const [submitLoading, setSubmitLoading] = useState(false);

	// Fetch configuration and providers on mount
	useEffect(() => {
		getGlobalLLMConfiguration();
		getLLMProviders();
	}, []);

	// Handle successful configuration fetch
	useEffect(() => {
		if (getGlobalLLMConfigurationState.apiState === 'success') {
			const data = getGlobalLLMConfigurationState.data || {};
			setFormData(data);
			form.setFieldsValue(data);
			getGlobalLLMConfigurationReset();
		}
	}, [getGlobalLLMConfigurationState.apiState]);

	// Populate available models when both formData and LLM providers are available
	useEffect(() => {
		if (getLLMProvidersState.apiState === 'success' && Object.keys(formData).length > 0) {
			const newAvailableModels = {};
			LLM_CONFIG_ITEMS.forEach((item) => {
				const providerValue = formData[item.providerField];
				if (providerValue) {
					const selectedProvider = getLLMProvidersState.data.find(
						(provider) => provider.provider === providerValue,
					);
					if (selectedProvider) {
						newAvailableModels[item.providerField] = selectedProvider.models.map(
							(model) => ({
								label: model.name,
								value: model.name,
							}),
						);
					}
				}
			});
			setAvailableModels(newAvailableModels);
		}
	}, [getLLMProvidersState.apiState, formData]);

	// Handle successful update
	useEffect(() => {
		if (updateGlobalLLMConfigurationState.apiState === 'success') {
			setSubmitLoading(false);
			notification.success({
				message: 'LLM settings saved successfully',
			});
			updateGlobalLLMConfigurationReset();
		}
		if (updateGlobalLLMConfigurationState.apiState === 'error') {
			setSubmitLoading(false);
			notification.error({
				message: 'Failed to save LLM settings',
			});
			updateGlobalLLMConfigurationReset();
		}
	}, [updateGlobalLLMConfigurationState]);

	// Handle error in fetching configuration
	useEffect(() => {
		if (getGlobalLLMConfigurationState.apiState === 'error') {
			notification.error({
				message: getGlobalLLMConfigurationState.message || 'Failed to fetch configuration',
			});
			getGlobalLLMConfigurationReset();
		}
	}, [getGlobalLLMConfigurationState.apiState]);

	// Handle error in fetching LLM providers
	useEffect(() => {
		if (getLLMProvidersState.apiState === 'error') {
			notification.error({
				message: 'Failed to fetch LLM providers',
			});
		}
	}, [getLLMProvidersState.apiState]);

	// Handle provider change - update available models
	const handleProviderChange = (value, providerField, modelField) => {
		// Update form data
		setFormData((prev) => ({
			...prev,
			[providerField]: value,
			[modelField]: '', // Reset model when provider changes
		}));
		form.setFieldValue(modelField, null);

		// Update available models for this provider
		if (getLLMProvidersState.apiState === 'success' && value) {
			const selectedProvider = getLLMProvidersState.data.find(
				(provider) => provider.provider === value,
			);
			if (selectedProvider) {
				const models = selectedProvider.models.map((model) => ({
					label: model.name,
					value: model.name,
				}));
				setAvailableModels((prev) => ({
					...prev,
					[providerField]: models,
				}));
				// Auto-select if only one model
				if (models.length === 1) {
					setFormData((prev) => ({
						...prev,
						[modelField]: models[0].value,
					}));
					form.setFieldValue(modelField, models[0].value);
				}
			} else {
				setAvailableModels((prev) => ({
					...prev,
					[providerField]: [],
				}));
			}
		} else {
			setAvailableModels((prev) => ({
				...prev,
				[providerField]: [],
			}));
		}
	};

	// Handle model change
	const handleModelChange = (value, modelField) => {
		setFormData((prev) => ({
			...prev,
			[modelField]: value,
		}));
	};

	// Handle form submit
	const handleSubmit = () => {
		form.validateFields()
			.then(() => {
				setSubmitLoading(true);
				updateGlobalLLMConfiguration(formData);
			})
			.catch((errorInfo) => {
				notification.error({
					message: 'Please fill all required fields',
				});
			});
	};

	// Get provider options from LLMProviders
	const getProviderOptions = () => {
		if (getLLMProvidersState.apiState === 'success' && getLLMProvidersState.data?.length > 0) {
			return getLLMProvidersState.data.map((provider) => ({
				label: provider.provider,
				value: provider.provider,
			}));
		}
		return [];
	};

	// Check if data is loading
	const isLoading = 
		getGlobalLLMConfigurationState.apiState === 'loading' || 
		getLLMProvidersState.apiState === 'loading' ||
		submitLoading;

	return (
		<XTheme2InnerCard className="mb-3" bodyStyle={{ padding: '20px' }}>
			<Spin spinning={isLoading}>
				<Row gutter={[15, 15]} className="align-items-center mb-4">
					<Col xs={24}>
						<h1 className="cardTitle mb-0" style={{ fontSize: '18px' }}>
							Global LLM Configuration
						</h1>
						<p style={{ color: '#666', marginTop: '8px' }}>
							Configure LLM providers and models for different AI operations. These settings apply globally to all users.
						</p>
					</Col>
				</Row>

				<XThemeForm
					form={form}
					name="globalLLMConfigForm"
					layout="vertical"
					autoComplete="off"
					size="large"
				>
					{LLM_CONFIG_ITEMS.map((item, index) => (
						<div key={item.providerField}>
							<Row gutter={[15, 15]} className="align-items-end">
								<Col xs={24}>
									<h2 style={{ 
										fontSize: '16px', 
										fontWeight: '600', 
										marginBottom: '16px',
										color: '#212226'
									}}>
										{item.label}
									</h2>
								</Col>
								<Col xs={24} sm={12} md={8} lg={6}>
									<XThemeForm.Item
										className="drawerlabel"
										name={item.providerField}
										label="Provider"
										rules={[
											{
												required: true,
												message: `Please select provider for ${item.label}`,
											},
										]}
									>
										<XThemeSelect
											className="largeInputForms"
											size="large"
											placeholder="Select Provider"
											showSearch={true}
											allowClear
											value={formData[item.providerField]}
											filterOption={(input, option) =>
												option.label
													.toLowerCase()
													.includes(input.toLowerCase())
											}
											options={getProviderOptions()}
											onChange={(value) =>
												handleProviderChange(
													value,
													item.providerField,
													item.modelField,
												)
											}
										/>
									</XThemeForm.Item>
								</Col>
								<Col xs={24} sm={12} md={8} lg={6}>
									<XThemeForm.Item
										className="drawerlabel"
										name={item.modelField}
										label="Model"
										rules={[
											{
												required: true,
												message: `Please enter model for ${item.label}`,
											},
										]}
									>
										<AutoComplete
											className="largeInputForms"
											size="large"
											placeholder="Select or Enter Model"
											allowClear
											value={formData[item.modelField] || ''}
											options={availableModels[item.providerField] || []}
											onChange={(value) => {
												handleModelChange(value, item.modelField);
												form.setFieldValue(item.modelField, value);
											}}
											filterOption={false}
											disabled={!formData[item.providerField]}
											style={{ width: '100%' }}
											popupMatchSelectWidth={true}
											defaultActiveFirstOption={false}
										/>
									</XThemeForm.Item>
								</Col>
							</Row>
							{index < LLM_CONFIG_ITEMS.length - 1 && (
								<Divider style={{ margin: '16px 0' }} />
							)}
						</div>
					))}

					<Row gutter={[15, 15]} className="mt-4">
						<Col xs={24} className="text-end">
							<Space>
								<XTheme2Button
									className="submitCancel px-4"
									shape="round"
									size="large"
									loading={submitLoading}
									onClick={handleSubmit}
									icon={<SaveOutlined />}
								>
									Save Configuration
								</XTheme2Button>
							</Space>
						</Col>
					</Row>
				</XThemeForm>
			</Spin>
		</XTheme2InnerCard>
	);
};

const mapStateToProps = (state) => ({
	getGlobalLLMConfigurationState: state.getGlobalLLMConfiguration,
	updateGlobalLLMConfigurationState: state.updateGlobalLLMConfiguration,
	getLLMProvidersState: state.getLLMProviders,
});

const mapDispatchToProps = (dispatch) => ({
	getGlobalLLMConfiguration: (params) => dispatch(getGlobalLLMConfigurationApi(params)),
	getGlobalLLMConfigurationReset: () => dispatch(getGlobalLLMConfigurationReset()),
	updateGlobalLLMConfiguration: (data) => dispatch(updateGlobalLLMConfigurationApi(data)),
	updateGlobalLLMConfigurationReset: () => dispatch(updateGlobalLLMConfigurationReset()),
	getLLMProviders: (params) => dispatch(getLLMProvidersApi(params)),
	getLLMProvidersReset: () => dispatch(getLLMProvidersReset()),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps,
)(GlobalLLMConfigurationManagement);

